import { useRef, useEffect, useState } from "react";
import Pagination from "../../../../Components/Pagination";
import Button from "../../../../Core/Button/Button";
import GetIcon from "../../../../utils/Icon";
import SearchableDropdown from "../../../../Core/Fields/Floating/SearchableDropdown";
import { useGetItemTaxTemplatesQuery, useGetTaxcategoryQuery } from "../../../../store/api/gsthsn";

const Table = ({ taxes }) => {
  // ✅ Controlled data from parent
  const { value: rowsRaw = [], setValue = () => {} } = taxes || {};
  const rows = Array.isArray(rowsRaw) ? rowsRaw : [];

  const tableRef = useRef();

  // ✅ Dropdown Refs
  const dropdownRefItemTax = useRef(null);
  const dropdownRefTaxCat = useRef(null);

  // ✅ Pagination
  const [rowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // ✅ UI states
  const [selectedRows, setSelectedRows] = useState([]);
  const [focusedCell, setFocusedCell] = useState({ rowIndex: null, field: null });
  const [showDropdown, setShowDropdown] = useState({ rowIndex: null, field: null });

  // ✅ Dropdown handling states
  const [offsetItemTax, setOffsetItemTax] = useState(0);
  const [offsetTaxCat, setOffsetTaxCat] = useState(0);
  const [searchItemTax, setSearchItemTax] = useState("");
  const [searchTaxCat, setSearchTaxCat] = useState("");
  const [hasMoreItemTax, setHasMoreItemTax] = useState(true);
  const [hasMoreTaxCat, setHasMoreTaxCat] = useState(true);

  // ✅ Dropdown data
  const [itemTaxOptions, setItemTaxOptions] = useState([]);
  const [taxCategoryOptions, setTaxCategoryOptions] = useState([]);

  const [previousCompany, setPreviousCompany] = useState("");

  // ✅ APIs
  const { data: itemTaxData } = useGetItemTaxTemplatesQuery();
  const { data: taxCatData } = useGetTaxcategoryQuery();

  // ✅ Load dropdown data
  useEffect(() => {
    if (itemTaxData?.data?.length) {
      setItemTaxOptions(itemTaxData.data.map((item) => ({ name: item.name })));
    } else {
      setItemTaxOptions([]);
    }
  }, [itemTaxData]);

  useEffect(() => {
    if (taxCatData?.data?.length) {
      setTaxCategoryOptions(taxCatData.data.map((item) => ({ name: item.name })));
    } else {
      setTaxCategoryOptions([]);
    }
  }, [taxCatData]);

  // ✅ Pagination logic
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = rows.slice(indexOfFirstRow, indexOfLastRow);

  // ✅ Row selection logic
  const isAllSelected = rows.length > 0 && selectedRows.length === rows.length;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(rows.map((_, index) => index));
    } else {
      setSelectedRows([]);
    }
  };

  const toggleRowSelection = (index) => {
    setSelectedRows((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  // ✅ Update field value
  const updateRow = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index] = { ...updatedRows[index], [field]: value };
    setValue(updatedRows);
  };

  // ✅ Add/Delete rows
  const handleAddRow = () => {
    const newRow = {
      item_tax_template: "",
      tax_category: "",
      valid_from: "",
      minimum_net_rate: "",
      maximum_net_rate: "",
    };
    const updated = [...rows, newRow];
    setValue(updated);
    const lastPage = Math.ceil(updated.length / rowsPerPage);
    if (currentPage !== lastPage) setCurrentPage(lastPage);
  };

  const handleDeleteSelectedRows = () => {
    const updatedRows = rows.filter((_, idx) => !selectedRows.includes(idx));
    setValue(updatedRows);
    setSelectedRows([]);
    const lastPage = Math.ceil(updatedRows.length / rowsPerPage);
    if (currentPage > lastPage) setCurrentPage(lastPage);
  };

  return (
    <div className="table-container">
      <table className="table table-bordered mb-1" ref={tableRef}>
        <thead className="tableHead1">
          <tr>
            <th style={{ width: "4%", textAlign: "center" }}>
              <input
                type="checkbox"
                className="form-check-input"
                checked={isAllSelected}
                onChange={handleSelectAll}
              />
            </th>
            <th style={{ width: "4%", textAlign: "center" }}>S.No</th>
            <th style={{ width: "160px" }}>Item Tax Template</th>
            <th style={{ width: "130px" }}>Tax Category</th>
            <th style={{ width: "130px" }}>Valid From</th>
            <th style={{ width: "120px" }}>Min Net Rate</th>
            <th style={{ width: "120px" }}>Max Net Rate</th>
            <th style={{ width: "80px", textAlign: "center" }}>
              <GetIcon iconName="PiSlidersHorizontalBold" size={12} />
            </th>
          </tr>
        </thead>

        <tbody>
          {currentRows.map((row, index) => {
            const globalIndex = indexOfFirstRow + index;

            return (
               <tr key={globalIndex}>
                {/* Select Checkbox */}
                <td style={{ width: '4%', textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={selectedRows.includes(globalIndex)}
                    onChange={() => toggleRowSelection(globalIndex)}
                    style={{
                      backgroundColor: selectedRows.includes(globalIndex)
                        ? "#270b79"
                        : "",
                      borderColor: selectedRows.includes(globalIndex)
                        ? "#270b79"
                        : "",
                      marginTop: "8px",
                    }}
                  />
                </td>

                {/* S.No */}
                <td style={{ width: '4%', textAlign: 'center' }}>{globalIndex + 1}</td>

                {/* ✅ Item Tax Template Dropdown */}
                <td className="p-0 position-relative">
                  <SearchableDropdown
                    globalIndex={globalIndex}
                    field="item_tax_template"
                    value={row.item_tax_template || ""}
                    onChange={(val) => updateRow(globalIndex, "item_tax_template", val)}
                    onSearch={(text) => setSearchItemTax(text)}
                    dropdownData={itemTaxOptions.filter((t) =>
                      t.name.toLowerCase().includes(searchItemTax.toLowerCase())
                    )}
                    placeholder="Select Item Tax Template"
                    focusedCell={focusedCell}
                    setFocusedCell={setFocusedCell}
                    showDropdown={showDropdown}
                    setShowDropdown={setShowDropdown}
                    dropdownRef={dropdownRefItemTax}
                    offset={offsetItemTax}
                    setOffset={setOffsetItemTax}
                    search_input={searchItemTax}
                    hasMore={hasMoreItemTax}
                    getData={() => setItemTaxOptions(itemTaxOptions)}
                  />
                </td>

                {/* ✅ Tax Category Dropdown */}
                <td className="p-0 position-relative">
                  <SearchableDropdown
                    globalIndex={globalIndex}
                    field="tax_category"
                    value={row.tax_category || ""}
                    onChange={(val) => updateRow(globalIndex, "tax_category", val)}
                    onSearch={(text) => setSearchTaxCat(text)}
                    dropdownData={taxCategoryOptions.filter((t) =>
                      t.name.toLowerCase().includes(searchTaxCat.toLowerCase())
                    )}
                    placeholder="Select Tax Category"
                    focusedCell={focusedCell}
                    setFocusedCell={setFocusedCell}
                    showDropdown={showDropdown}
                    setShowDropdown={setShowDropdown}
                    dropdownRef={dropdownRefTaxCat}
                    offset={offsetTaxCat}
                    setOffset={setOffsetTaxCat}
                    search_input={searchTaxCat}
                    hasMore={hasMoreTaxCat}
                    getData={() => setTaxCategoryOptions(taxCategoryOptions)}
                  />
                </td>

                {/* ✅ Valid From */}
                <td className="p-0 text-center">
                  <input
                    type="date"
                    placeholder="Valid from"
                    className="form-control tableinput noborder"
                    value={row.valid_from || ""}
                    style={{ fontSize: "0.8rem", textAlign: "center" }}
                    onChange={(e) => updateRow(globalIndex, "valid_from", e.target.value)}
                  />
                </td>

                {/* ✅ Min Net Rate */}
                
                <td className="p-0 text-center">
                  <input
                    type="number"
                    placeholder="Minimum Net Rate"
                    className="form-control tableinput noborder"
                    value={row?.minimum_net_rate || ""}
                    style={{ fontSize: "0.8rem", textAlign: "center" }}
                    onChange={(e) =>
                      updateRow(globalIndex, "minimum_net_rate", e.target.value)
                    }
                  />
                </td>

                {/* ✅ Max Net Rate */}
                <td className="p-0 text-center">
                  <input
                    type="number"
                    placeholder="Maximum Net Rate"
                    className="form-control tableinput noborder"
                    value={row?.maximum_net_rate || ""}
                    style={{ fontSize: "0.8rem", textAlign: "center" }}
                    onChange={(e) =>
                      updateRow(globalIndex, "maximum_net_rate", e.target.value)
                    }
                  />
                </td>
               

                 <td
                  style={{
                    position: "relative",
                    width: "40px",
                  }}
                >
                  <img
                    src="/Maskgroup.png"
                    alt="edit"
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "12px",
                      height: "12px",
                      pointerEvents: "none",
                    }}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

       <div className="d-flex pb-2 justify-content-between align-items-center mt-3">
        <div className="d-flex" style={{ gap: "4px" }}>
          {selectedRows.length > 0 ? (
            <Button
              onClick={handleDeleteSelectedRows}
              className="customdeletebtn"
              iconName="MdDeleteOutline"
              iconSize={12}
            >
              Delete
            </Button>
          ) : (
            <Button
              onClick={handleAddRow}
              className="Addrowdesign"
              iconName="FiPlus"
              iconSize={12}
              iconWrapperStyle={{
                backgroundColor: "#F6D593",
                padding: "1px",
                display: "flex",
              }}
            >
              Add Row
            </Button>
          )}
        </div>

        {/* Pagination */}
        <div
          className={`${
            rows.length < 11 ? "d-none" : "d-flex"
          } align-items-center centerpagination`}
          style={{ gap: "5px" }}
        >
          <Pagination
            setCurrentPage={setCurrentPage}
            currentPage={currentPage}
            pageSize={rowsPerPage}
            totalrecords={rows.length}
          />
        </div>

        {/* Entry Count */}
        <div className={`${rows.length < 11 ? "d-none" : "d-flex"}`}>
          <span className="px-2" style={{ fontSize: "12px", color: "#270B79" }}>
            Showing {indexOfFirstRow + 1} to{" "}
            {Math.min(indexOfLastRow, rows.length)} of {rows.length} entries
          </span>
        </div>
      </div>
    </div>
  );
};

export default Table;
